import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonList } from '@ionic/angular/standalone';
import { LoadingButtonComponent } from '../loading-button/loading-button.component';

/**
 * Wrapper around `ion-list` with shared defaults (`lines="full"`, `ion-no-padding`).
 * Use instead of raw `ion-list` so list chrome stays consistent app-wide.
 *
 * When using {@link showLoadMore}, keep `crowdctrl-pagination-footer` as a direct child of this
 * component so it projects below the load-more control.
 */
@Component({
  selector: 'crowdctrl-list',
  standalone: true,
  imports: [CommonModule, IonList, LoadingButtonComponent],
  templateUrl: './list.component.html',
  styleUrl: './list.component.css',
})
export class CrowdctrlListComponent {
  /** Passed through to `ion-list` (`full`, `inset`, `none`, etc.). Default `full`. */
  @Input() lines = 'full';
  @Input() inset?: boolean;
  @Input() mode?: 'ios' | 'md';

  /** When true (default), applies Ionic `ion-no-padding` on the inner list. */
  @Input() noPadding = true;

  /** Additional CSS classes on the inner `ion-list` (e.g. `orders-list`). */
  @Input() listClass?: string;

  /**
   * When true, the list cannot be interacted with (uses `inert` where supported,
   * plus non-interactive styling on `ion-list`).
   */
  @Input() disabled = false;

  /**
   * When true, renders a built-in “Load more” control between the default projected rows and
   * any `crowdctrl-pagination-footer` child.
   * The button stays visible but is disabled when {@link loadMoreHasNext} is false (instead of
   * hiding), which helps verify paging wiring against the API.
   */
  @Input() showLoadMore = false;

  /** When false, the load-more control is disabled (still shown if {@link showLoadMore}). */
  @Input() loadMoreHasNext = false;

  @Input() loadMoreLoading = false;

  @Input() loadMoreLabel = 'Load More';

  @Input() loadMoreLoadingText = 'Loading...';

  @Output() loadMoreClick = new EventEmitter<void>();

  get hostClassString(): string {
    const parts: string[] = [];
    if (this.noPadding) {
      parts.push('ion-no-padding');
    }
    const extra = this.listClass?.trim();
    if (extra) {
      parts.push(extra);
    }
    return parts.join(' ');
  }
}
